import Color from "color";

// https://github.com/Qix-/color/blob/e188999dee229c902102ec37e398ff4d868616e5/index.js#L298
export const isDark = (color: InstanceType<typeof Color>, threshold = 145) => {
  const [r = 0, g = 0, b = 0] = color.rgb().array();
  const yiq = (r * 2126 + g * 7152 + b * 722) / 10000;
  return yiq < threshold;
};

export const loadGoogleFont = (
  fontFamily: string,
  weights: number[] = [400, 500, 600, 700],
) => {
  const fontId = `google-font-${fontFamily.replace(/\s+/g, "-")}`;

  // Prevent duplicate loads
  // Ensure that the html template sets the id of the main font on its link tag (e.g., id="google-font-Inter") to avoid double loading
  // Currently our preferred font is Inter
  if (document.getElementById(fontId)) return;

  const link = document.createElement("link");
  link.id = fontId;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
    /\s+/g,
    "+",
  )}:wght@${weights.join(";")}&display=swap`;
  document.head.appendChild(link);
};

type ContrastDirection = "auto" | "lighten" | "darken";
type ContrastStrategy = "hsl" | "opacity";

export type ToContrastColorOptions = {
  /** WCAG contrast ratio to aim for (1 = identical, 21 = black/white). */
  targetContrast?: number;
  /**
   * Which way to shift. `auto` lightens dark colors and darkens light colors
   * (wash / surface). With `backdrop`, `auto` moves away from the backdrop
   * (darken on light surfaces, lighten on dark). Use explicit `darken`/`lighten`
   * for emphasize.
   */
  direction?: ContrastDirection;
  /**
   * `hsl` — lerp lightness toward white/black (opaque).
   * `opacity` — mix primary over white (`lighten`) or black (`darken`);
   * white path returns an alpha colour.
   */
  strategy?: ContrastStrategy;
  /**
   * Surface the result must contrast against. When set, contrast is measured
   * as `result.contrast(backdrop)` instead of `primary.contrast(result)`.
   * If `primary` already meets `targetContrast` vs the backdrop, it is
   * returned unchanged (e.g. brand ink on white).
   */
  backdrop?: string;
  /**
   * Lower bound on the search parameter.
   * HSL: fraction of the way toward white/black lightness (default 0).
   * Opacity: minimum primary mix weight / alpha (default 0.1).
   */
  minAmount?: number;
  /**
   * Upper bound on the search parameter.
   * HSL: max fraction toward white/black lightness (default 1).
   * Opacity: max primary mix weight (default 1).
   */
  maxAmount?: number;
  iterations?: number;
};

/** Floors so soft/strong steps always retain visible fill colour. */
const MIN_SOFT_AMOUNT = 0.1;
const MIN_EMPHASIZE_PRIMARY_MIX = 0.15;
const EMPHASIZE_LIGHTNESS_MIN = 5;
const EMPHASIZE_LIGHTNESS_MAX = 95;

/**
 * Shared binary search: find a parameter in [minAmount, maxAmount] so
 * `against.contrast(adjust(t))` meets `targetContrast`.
 *
 * - `prefer: "min"` — smallest t that meets (HSL distance from base).
 * - `prefer: "max"` — largest t that meets (opacity primary weight).
 * If none meet, returns `fallback` (`min` or `max` end).
 */
function searchContrastAmount(
  against: InstanceType<typeof Color>,
  adjust: (t: number) => InstanceType<typeof Color>,
  {
    targetContrast,
    minAmount,
    maxAmount,
    prefer,
    fallback,
    iterations,
  }: {
    targetContrast: number;
    minAmount: number;
    maxAmount: number;
    prefer: "min" | "max";
    fallback: "min" | "max";
    iterations: number;
  },
): number {
  const meets = (t: number) => against.contrast(adjust(t)) >= targetContrast;

  if (prefer === "min") {
    if (meets(minAmount)) {
      return minAmount;
    }
    if (!meets(maxAmount)) {
      return fallback === "min" ? minAmount : maxAmount;
    }
    let lo = minAmount;
    let hi = maxAmount;
    let best = maxAmount;
    for (let i = 0; i < iterations; i++) {
      const mid = (lo + hi) / 2;
      if (meets(mid)) {
        hi = mid;
        best = mid;
      } else {
        lo = mid;
      }
    }
    return best;
  }

  // prefer max (e.g. opacity primary weight)
  if (!meets(minAmount)) {
    return fallback === "min" ? minAmount : maxAmount;
  }
  if (meets(maxAmount)) {
    return maxAmount;
  }
  let lo = minAmount;
  let hi = maxAmount;
  let best = minAmount;
  for (let i = 0; i < iterations; i++) {
    const mid = (lo + hi) / 2;
    if (meets(mid)) {
      lo = mid;
      best = mid;
    } else {
      hi = mid;
    }
  }
  return best;
}

function resolveDirection(
  base: InstanceType<typeof Color>,
  direction: ContrastDirection,
  backdrop?: InstanceType<typeof Color>,
): "lighten" | "darken" {
  if (direction !== "auto") {
    return direction;
  }
  // Against a surface: move away from it (ink on white → darken).
  if (backdrop) {
    return backdrop.isLight() ? "darken" : "lighten";
  }
  return base.isLight() ? "darken" : "lighten";
}

function contrastByHsl(
  base: InstanceType<typeof Color>,
  dir: "lighten" | "darken",
  {
    targetContrast,
    minAmount,
    maxAmount,
    iterations,
    against,
  }: {
    targetContrast: number;
    minAmount: number;
    maxAmount: number;
    iterations: number;
    against: InstanceType<typeof Color>;
  },
) {
  // Prefer lightness lerp over color.lighten/darken: those multiply L, so
  // pure/near-black (L≈0) never moves and contrast search is a no-op.
  const baseL = base.lightness();
  const targetL = dir === "lighten" ? 100 : 0;
  const adjust = (t: number) => {
    const amount = Math.min(1, Math.max(0, t));
    return base.lightness(baseL + (targetL - baseL) * amount);
  };

  const t = searchContrastAmount(against, adjust, {
    targetContrast,
    // HSL amount is a 0–1 fraction;
    minAmount: Math.min(1, Math.max(0, minAmount)),
    maxAmount: Math.min(1, Math.max(0, maxAmount)),
    prefer: "min",
    // Match legacy: if target unreachable, take the extreme shift.
    fallback: "max",
    iterations,
  });

  return adjust(t);
}

function contrastByOpacity(
  base: InstanceType<typeof Color>,
  dir: "lighten" | "darken",
  {
    targetContrast,
    minAmount,
    maxAmount,
    iterations,
    against,
  }: {
    targetContrast: number;
    minAmount: number;
    maxAmount: number;
    iterations: number;
    against: InstanceType<typeof Color>;
  },
) {
  const mixSurface = dir === "lighten" ? Color("#fff") : Color("#000");
  const mixOver = (weight: number) => mixSurface.mix(base, weight);

  const weight = searchContrastAmount(against, mixOver, {
    targetContrast,
    minAmount,
    maxAmount,
    prefer: "max",
    // Chroma floor wins over contrast when unreachable.
    fallback: "min",
    iterations,
  });

  // White wash → true opacity mixin; black mix → opaque (alpha-over-white
  // cannot produce the darkened soft surface light fills need).
  if (dir === "lighten") {
    return base.alpha(weight);
  }
  return mixOver(weight);
}

/**
 * WCAG target for "on" colours against a user-picked fill.
 *
 * Mid brand colours stay near `base` (tinted sibling). Near-black / near-white
 * fills ramp toward the maximum possible mono contrast so text does not land
 * on a muddy mid-grey. Extremity uses max(contrast vs white, vs black) — more
 * reliable than HSL lightness (e.g. yellow is L≈50 but still extreme).
 * `power` ≥ ~4 keeps the ramp flat through typical brand colours and only
 * steepens near true black/white.
 */
export const adaptiveContrastTarget = (
  fill: string,
  {
    base = 4.5,
    max = 21,
    power = 4,
  }: { base?: number; max?: number; power?: number } = {},
) => {
  const color = Color(fill).alpha(1);
  const maxPossible = Math.max(
    color.contrast(Color("#fff")),
    color.contrast(Color("#000")),
  );
  const span = 21 - base;
  const extremity =
    span <= 0
      ? 0
      : Math.min(1, Math.max(0, (maxPossible - base) / span)) ** power;
  return Math.min(maxPossible, base + (max - base) * extremity);
};

/**
 * Derives a colour a controlled contrast distance from `primary`.
 *
 * Binary-searches either HSL lightness toward white/black (`strategy: "hsl"`)
 * or an opacity mix over white/black (`strategy: "opacity"`) until
 * `targetContrast` is met — never further than needed (subject to
 * `minAmount` / `maxAmount`).
 *
 * By default contrast is measured between `primary` and the result. Pass
 * `backdrop` to measure the result against a surface instead (e.g. ink on
 * white); if `primary` already meets the target vs that surface it is kept.
 */
export const toContrastColor = (
  primary: string,
  {
    targetContrast = 3.1,
    direction = "auto",
    strategy = "hsl",
    backdrop,
    minAmount,
    maxAmount,
    iterations = 25,
  }: ToContrastColorOptions = {},
) => {
  const base = Color(primary).alpha(1);
  const ground = backdrop ? Color(backdrop).alpha(1) : undefined;
  const dir = resolveDirection(base, direction, ground);
  const against = ground ?? base;

  if (strategy === "opacity") {
    return contrastByOpacity(base, dir, {
      targetContrast,
      minAmount: minAmount ?? MIN_SOFT_AMOUNT,
      maxAmount: maxAmount ?? 1,
      iterations,
      against,
    });
  }

  return contrastByHsl(base, dir, {
    targetContrast,
    minAmount: minAmount ?? 0,
    maxAmount: maxAmount ?? 1,
    iterations,
    against,
  });
};

/**
 * Emphasize a fill for interaction (hover/active): prefer darken; if that
 * cannot reach `targetContrast`, fall back to lighten (near-black fills).
 * Never returns pure white/black.
 */
export const emphasizeColor = (
  primary: string,
  { targetContrast = 1.15 }: { targetContrast?: number } = {},
) => {
  const base = Color(primary).alpha(1);
  const darkened = toContrastColor(primary, {
    targetContrast,
    direction: "darken",
    strategy: "hsl",
  });
  const raw =
    base.contrast(darkened) >= targetContrast
      ? darkened
      : toContrastColor(primary, {
          targetContrast,
          direction: "lighten",
          strategy: "hsl",
        });

  return retainPrimaryChroma(base, raw);
};

/** Pull away from pure white/black while preferring the primary's identity. */
function retainPrimaryChroma(
  base: InstanceType<typeof Color>,
  candidate: InstanceType<typeof Color>,
) {
  const hex = candidate.hex().toLowerCase();
  const lightness = candidate.lightness();
  const tooDark = hex === "#000000" || lightness < EMPHASIZE_LIGHTNESS_MIN;
  const tooLight = hex === "#ffffff" || lightness > EMPHASIZE_LIGHTNESS_MAX;

  if (!tooDark && !tooLight) {
    return candidate;
  }

  // Mixing primary into itself does nothing when primary is already #000/#fff —
  // nudge toward the opposite extreme instead (keeps a visible non-pure step).
  if (tooDark) {
    return base.mix(Color("#fff"), MIN_EMPHASIZE_PRIMARY_MIX);
  }
  return base.mix(Color("#000"), MIN_EMPHASIZE_PRIMARY_MIX);
}
